import moment, {type Moment} from 'moment-timezone'
import {mapMaybes, maybe} from '@freckle/maybe'
import type {ParserT} from '@freckle/parser'
import {
  Parser,
  field,
  number,
  string,
  oneOf,
  array,
  boolean,
  nullable,
  date,
  record,
  mapStatic
} from '@freckle/parser'
import {fancyClient, extractData} from '@freckle/educator-entities/ts/openapi-clients'
import type {IdentityProviderT} from '@freckle/educator-entities/ts/roster/identity-provider'
import {parser as identityProviderParser} from '@freckle/educator-entities/ts/roster/identity-provider'

type StarStatus = 'star_enabled' | 'star_disabled' | 'star_unset'

const allStarStatuses: StarStatus[] = ['star_enabled', 'star_disabled', 'star_unset']

type RenaissanceSchoolClientT = {
  platform: string
  clientId: string
  rpid: string
  baseUrl: string
  starStatus: StarStatus
  sharedRosteringEnabled: boolean
}
export type SchoolAttrs = {
  id: number
  name: string
  createdAt: Moment
  updatedAt: Moment
  districtId: number
  sisId: string | undefined | null
  ncesSchoolId: string | undefined | null
  cleverSchoolId: string | undefined | null
  classLinkSchoolId: string | undefined | null
  renaissanceRpIdentifier: string | undefined | null
  city: string
  administrativeArea: string | undefined | null
  postalCode: string | undefined | null
  formattedAddressLines: Array<string> | undefined | null
  countryCode: string
  licenseUtilizationNotificationsMutedAt: Moment | undefined | null
  courseSubjectsByLicensing: boolean
  renaissanceSchoolClient: RenaissanceSchoolClientT | undefined | null
  activeIdp: IdentityProviderT | undefined | null
  syncIdp: boolean
  crmId: string | undefined | null
  active: boolean // a school can be active/inactive in RL CRM. As that becomes our source-of-truth, we mirror that data.
  crmAccountNumber: number | undefined | null
  identityProvider: IdentityProviderT | undefined | null
  idpManaged: boolean
}

const parseRenaissanceSchoolClient: ParserT<RenaissanceSchoolClientT> = record({
  platform: string(),
  clientId: string(),
  rpid: string(),
  baseUrl: string(),
  starStatus: oneOf<StarStatus>('StarStatus', allStarStatuses),
  sharedRosteringEnabled: boolean()
})

const parseSchoolAttrs: ParserT<SchoolAttrs> = record({
  id: number(),
  name: string(),
  createdAt: mapStatic(date(), moment),
  updatedAt: mapStatic(date(), moment),
  districtId: number(),
  sisId: nullable(string()),
  ncesSchoolId: nullable(string()),
  cleverSchoolId: nullable(string()),
  classLinkSchoolId: nullable(string()),
  renaissanceRpIdentifier: field(nullable(string()), 'renaissanceRPIdentifier'),
  city: string(),
  administrativeArea: nullable(string()),
  postalCode: nullable(string()),
  formattedAddressLines: nullable(array(string())),
  countryCode: string(),
  licenseUtilizationNotificationsMutedAt: nullable(mapStatic(date(), moment)),
  courseSubjectsByLicensing: boolean(),
  renaissanceSchoolClient: nullable(parseRenaissanceSchoolClient),
  activeIdp: nullable(identityProviderParser),
  syncIdp: boolean(),
  crmId: nullable(string()),
  active: boolean(),
  crmAccountNumber: nullable(number()),
  identityProvider: nullable(identityProviderParser),
  idpManaged: boolean()
})

export const parseSchool = Parser.mkRun<SchoolAttrs>(parseSchoolAttrs)

const parseSchools = Parser.mkRun<Array<SchoolAttrs>>(array(parseSchoolAttrs))
export function schoolHasSharedRostering(schoolAttrs: SchoolAttrs): boolean {
  return maybe(
    () => false,
    client => client.sharedRosteringEnabled,
    schoolAttrs.renaissanceSchoolClient
  )
}

// Subtype to support DetailedSchoolT, BasicSchoolT, etc used in Console:
type SchoolRenaissanceSchoolClientFieldsT = Inexact<{
  renaissanceSchoolClient: RenaissanceSchoolClientT | undefined | null
}>

export const schoolHasStarIntegration = (
  schoolAttrs: SchoolRenaissanceSchoolClientFieldsT
): boolean => schoolAttrs.renaissanceSchoolClient?.starStatus === 'star_enabled'

export function getActiveIdps(schools: Array<SchoolAttrs>): Set<IdentityProviderT> {
  return new Set(mapMaybes(schools, school => school.activeIdp))
}

export async function fetchSchools(): Promise<Array<SchoolAttrs>> {
  return fancyClient.GET('/2/schools').then(extractData).then(parseSchools)
}

export async function fetchSchool(id: number): Promise<SchoolAttrs> {
  return fancyClient
    .GET('/2/schools/{SchoolP-SchoolId}', {
      params: {
        path: {
          'SchoolP-SchoolId': id
        }
      }
    })
    .then(extractData)
    .then(parseSchool)
}

export async function fetchSchoolsInDistrict(districtId: number): Promise<Array<SchoolAttrs>> {
  return fancyClient
    .GET('/2/districts/{DistrictP-DistrictId}/schools', {
      params: {
        path: {
          'DistrictP-DistrictId': districtId
        }
      }
    })
    .then(extractData)
    .then(parseSchools)
}
